/* eslint-disable react-refresh/only-export-components */
import circleWarningIcon from '@/assets/icons/Circle_Warning.png';
import { Box, Button, Modal, TextField, Typography } from '@mui/material';

// =============================================================================
// RESPONSIVE SIZING CONSTANTS
// =============================================================================

// Section header bar
export const SECTION_HEADER_WIDTH = 'clamp(320px, 30.68vw, 589px)';
const SECTION_HEADER_HEIGHT = 'clamp(30px, 5vh, 54px)';
const SECTION_TITLE_SIZE = 'clamp(12px, 1.1vw, 28px)';

// Field label and value sizing
export const LABEL_SIZE = 'clamp(10px, 1.0vw, 23px)'; // Smaller min for laptops
export const VALUE_SIZE = 'clamp(10px, 1.0vw, 23px)'; // Smaller min for laptops

// Input field sizing
const INPUT_WIDTH = 'clamp(100px, 8.96vw, 172px)';
const INPUT_HEIGHT = 'clamp(24px, 3.24vh, 35px)';

// Tag input box sizing
export const TAG_INPUT_HEIGHT = 'clamp(28px, 5vh, 55px)';
export const TAG_BUTTON_WIDTH = 'clamp(110px, 14.5vw, 280px)';

// Tag styling
export const TAG_HEIGHT = 'clamp(22px, 3.5vh, 38px)';
export const TAG_FONT = 'clamp(10px, 1vw, 16px)';
const BUTTON_HEIGHT = 'clamp(18px, 3vh, 32px)';

// Permission modal sizing - smaller on laptops
const MODAL_WIDTH = 'clamp(320px, 33.33vw, 640px)';
const MODAL_HEIGHT = 'clamp(150px, 25vh, 270px)';
const MODAL_ICON_SIZE = 'clamp(32px, 3.13vw, 60px)';
const MODAL_TEXT_SIZE = 'clamp(11px, 1.15vw, 22px)';
const MODAL_BUTTON_WIDTH = 'clamp(60px, 6.25vw, 120px)';
const MODAL_BUTTON_HEIGHT = 'clamp(26px, 4.44vh, 48px)';
const MODAL_BUTTON_FONT = 'clamp(11px, 1.15vw, 22px)';

// Colors from design system
export const COLORS = {
  containerBg: 'rgba(213, 228, 213, 1)',
  sectionHeaderBg: '#0E463F',
  sectionHeaderText: '#FFFFFF',
  inputBg: '#003D36',
  inputBorder: '#7BBCB3',
  inputText: '#FFFFFF',
  labelText: 'rgba(8, 50, 55, 1)',
  valueText: 'rgba(8, 50, 55, 1)',
  buttonBg: '#0E463F',
  buttonHover: '#0a5c52',
  avatarBg: '#D5EBE1',
  avatarBorder: '#0E463F',
  tagBg: '#7BBCB3',
  tagText: '#000000',
  addButtonBg: '#7BBCB3',
  addButtonText: '#0E463F',
};

// Input field styles matching design system
export const inputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(14, 70, 63, 0.47)',
    borderRadius: 0,
    fontFamily: '"Inter", sans-serif',
    height: INPUT_HEIGHT,
    minHeight: INPUT_HEIGHT,
    maxHeight: INPUT_HEIGHT,
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#000000',
    fontFamily: '"Inter", sans-serif',
    fontSize: VALUE_SIZE,
    fontWeight: 650,
    padding: 'clamp(6px, 0.8vh, 10px) clamp(12px, 1vw, 16px)',
    height: '100%',
    boxSizing: 'border-box',
    '&::placeholder': {
      color: '#000000',
      opacity: 0.4,
      fontWeight: 500,
    },
  },
};

// =============================================================================
// COMPONENTS
// =============================================================================

interface SectionHeaderProps {
  title: string;
  width?: string;
}

export function SectionHeader({ title, width }: SectionHeaderProps) {
  const headerWidth = width || SECTION_HEADER_WIDTH;
  return (
    <Box
      sx={{
        backgroundColor: COLORS.sectionHeaderBg,
        width: headerWidth,
        minWidth: headerWidth,
        flexShrink: 0,
        height: SECTION_HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        px: 'clamp(12px, 1.5vw, 20px)',
        borderRadius: 0,
      }}
    >
      <Typography
        sx={{
          color: COLORS.sectionHeaderText,
          fontFamily: '"Inter", sans-serif',
          fontWeight: 700,
          fontSize: SECTION_TITLE_SIZE,
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

interface FieldDisplayProps {
  label: string;
  value: string | undefined;
}

export function FieldDisplay({ label, value }: FieldDisplayProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(8px, 1.2vh, 16px)',
      }}
    >
      <Typography
        sx={{
          color: COLORS.labelText,
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          fontSize: LABEL_SIZE,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: COLORS.valueText,
          fontFamily: '"Inter", sans-serif',
          fontWeight: 650,
          fontSize: VALUE_SIZE,
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  );
}

interface FieldEditProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function FieldEdit({
  label,
  value,
  onChange,
  placeholder,
}: FieldEditProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(12px, 1.7vh, 20px)',
        alignItems: 'flex-start',
      }}
    >
      <Typography
        sx={{
          color: COLORS.labelText,
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          fontSize: LABEL_SIZE,
        }}
      >
        {label}
      </Typography>
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        sx={{
          ...inputStyles,
          width: INPUT_WIDTH,
        }}
      />
    </Box>
  );
}

interface PermissionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  message: string;
}

export function PermissionModal({
  open,
  onClose,
  onConfirm,
  message,
}: PermissionModalProps) {
  // Shared button styles for YES and NO buttons
  const modalButtonStyles = {
    backgroundColor: 'transparent !important',
    height: MODAL_BUTTON_HEIGHT,
    width: MODAL_BUTTON_WIDTH,
    borderRadius: 0,
    fontFamily: '"Futura PT Book", "Futura PT", "Futura", sans-serif',
    fontSize: MODAL_BUTTON_FONT,
    fontWeight: 400,
    textTransform: 'none',
    border: 'none',
    padding: 0,
    minWidth: 0,
    '& span': {
      color: '#FFFFFF',
      transition: 'color 0.2s ease',
    },
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    '&:active': {
      backgroundColor: 'transparent !important',
    },
    '&:hover span': {
      color: '#7BBCB3',
    },
    '&:active span': {
      color: 'rgba(200, 220, 215, 1)',
    },
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: MODAL_WIDTH,
          height: MODAL_HEIGHT,
          backgroundColor: 'rgba(14, 70, 63, 1)',
          border: '2px solid #7BBCB3',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 'clamp(11px, 1.48vh, 16px)',
          outline: 'none',
        }}
      >
        {/* Warning icon - at top */}
        <Box
          sx={{
            width: MODAL_ICON_SIZE,
            height: MODAL_ICON_SIZE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 'clamp(4px, 0.5vh, 8px)',
            marginBottom: 'clamp(0px, -2.4vh, -28px)',
          }}
        >
          <img
            src={circleWarningIcon}
            alt='Warning'
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Message text - below icon, moved up */}
        <Typography
          sx={{
            color: '#FFFFFF',
            fontFamily: '"Futura PT Book", "Futura PT", "Futura", sans-serif',
            fontWeight: 400,
            fontSize: MODAL_TEXT_SIZE,
            textAlign: 'center',
            letterSpacing: '0.5px',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            mb: 1
          }}
        >
          {message}
        </Typography>

        {/* YES and NO buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 'clamp(160px, 16vw, 250px)',
            width: '100%',
            justifyContent: 'center',
            marginBottom: 'clamp(5px, 1vh, 12px)',
          }}
        >
          <Button onClick={onConfirm} disableRipple sx={modalButtonStyles}>
            <span>YES</span>
          </Button>
          <Button onClick={onClose} disableRipple sx={modalButtonStyles}>
            <span>NO</span>
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

interface AddTagButtonProps {
  label: string;
  options: string[];
  onSelect: (value: string) => void;
  showDropdown: boolean;
  onToggleDropdown: () => void;
}

export function AddTagButton({
  label,
  options,
  onSelect,
  showDropdown,
  onToggleDropdown,
}: AddTagButtonProps) {
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Add button - dark teal background, no rounded corners */}
      <Box
        onClick={onToggleDropdown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0E463F',
          color: '#FFFFFF',
          height: BUTTON_HEIGHT,
          width: TAG_BUTTON_WIDTH,
          px: 'clamp(8px, 0.8vw, 12px)',
          borderRadius: 0,
          fontFamily: '"Inter", sans-serif',
          fontSize: TAG_FONT,
          fontWeight: 600,
          cursor: 'pointer',
          boxSizing: 'border-box',
          '&:hover': {
            backgroundColor: '#0a5c52',
          },
        }}
      >
        {label}
      </Box>

      {/* Dropdown menu - matches input box background, same width as button */}
      {showDropdown && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            mt: 0,
            backgroundColor: 'rgba(14, 70, 63, 0.47)',
            borderRadius: 0,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            width: TAG_BUTTON_WIDTH,
            maxHeight: 'clamp(95px, 14vh, 150px)',
            overflowY: 'auto',
          }}
        >
          {options.map((option) => (
            <Box
              key={option}
              onClick={() => onSelect(option)}
              sx={{
                px: 'clamp(10px, 1vw, 14px)',
                py: 'clamp(6px, 0.8vh, 9px)',
                cursor: 'pointer',
                fontFamily: '"Inter", sans-serif',
                fontSize: TAG_FONT,
                fontWeight: 600,
                color: '#000000',
                textAlign: 'center',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              }}
            >
              {option}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
